/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { useClientDocuments } from '../../hooks/useClientDocuments';
import { ClientDocumentsTab } from './ClientDocumentsTab';

interface ClientDocumentsPanelProps {
  agencyId: string;
  clientId: string;
}

/**
 * Self-contained documents tab: owns the documents hook and feeds the
 * presentational {@link ClientDocumentsTab}.
 *
 * Callers that already hold the documents state pass it straight to
 * `ClientDocumentsTab`; this container exists for callers that render the tab
 * lazily, so the fetch only runs when the tab is actually opened.
 *
 * @param props - The agency and client identifiers.
 * @returns The rendered documents tab.
 */
export const ClientDocumentsPanel: React.FC<ClientDocumentsPanelProps> = ({
  agencyId,
  clientId,
}) => {
  const { t } = useTranslation('features/clients');
  const {
    documents, loading, error, uploadDocument, deleteDocument, renameDocument,
  } = useClientDocuments(agencyId, clientId);

  const handleUpload = (file: File) => {
    // `uploadDocument` resolves false when the presigned link is missing or
    // unusable; without this the upload would fail with no trace on screen.
    void uploadDocument(file)
      .then((ok) => {
        if (ok) return;
        notifications.show({
          type: 'error',
          title: t('profile.documents.uploadErrorTitle'),
          message: t('profile.documents.uploadErrorMessage'),
        });
      })
      .catch(() => {
        notifications.show({
          type: 'error',
          title: t('profile.documents.uploadErrorTitle'),
          message: t('profile.documents.uploadErrorMessage'),
        });
      });
  };

  return (
    <ClientDocumentsTab
      documents={documents}
      loading={loading}
      error={error}
      onUpload={handleUpload}
      onDelete={deleteDocument}
      onRename={(documentId, filename) => { void renameDocument(documentId, filename); }}
    />
  );
};
